import './styles.scss';

import { CST, EST, MST, PST } from '../Icons';
import { Col, Row } from 'react-bootstrap';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import NavigationDrawer from '@/components/Header/NavigationDrawer';
import { QUICK_ROUTES } from '@/constants';
import { Route } from '@/models';
import Shortcut from './Shortcut';
import TimeZone from '@/components/TimeZone';
import useCssVariable from '@/hooks/useCssVariable';
import useMutationObserver from '@/hooks/useMutationObserver';
import useSession from '@/state/session';

const Header = ({ filters: Filters }: { filters?: () => JSX.Element }): JSX.Element => {
  const [title, setTitle] = useState(document.title);
  const [toolbarInfo, setToolbarInfo] = useState(undefined);
  const firstName = useSession(({ state }) => state.user?.firstName || '');
  const menuList = useSession(({ state }) => state?.config?.userConfig?.menus?.shortcuts || QUICK_ROUTES || []);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useCssVariable('--header-height');

  const quickRoutes = useMemo(
    () =>
      (menuList || []).map((route: Route, index: number): React.JSX.Element => {
        if (route.url === '/availability') return <Shortcut.Availability {...route} key={index} />;
        return <Shortcut {...route} icon={typeof route?.icon === 'string' ? <i className={route?.icon} /> : route?.icon} key={index} />;
      }),
    [menuList]
  );

  useEffect((): void => {
    setTimeout((): void => setTitle(document.title), 0);
  }, []);
  useMutationObserver(headerRef, (): void => {
    const height = `${headerRef.current?.offsetHeight || 0}px`;
    if (headerHeight === height) return;
    setHeaderHeight(height);
  });

  return (
    <header className="Header" ref={headerRef}>
      <Row className="HeaderNavigation">
        <Col className="d-flex flex-grow-0">
          <NavigationDrawer />
        </Col>
        <Col>
          {quickRoutes?.length > 0 && (
            <div className="NavLinks d-flex justify-content-around flex-nowrap flex-grow-0 rounded {gap:26px;}">{quickRoutes}</div>
          )}
        </Col>
        <Col className="position-relative flex-grow-1">
          <div className="text-secondary-subtle d-flex flex-column gap-2 text-end m-0 {white-space:nowrap;top:0;right:0;}">
            <span>Welcome {firstName}</span>
            <span>{title}</span>
          </div>
        </Col>
      </Row>
      <Row className="HeaderFilters">
        {!Filters && <Col id="PageFilters" className="d-flex flex-column" />}
        {Filters && (
          <Col className="d-flex flex-column">
            <Filters />
          </Col>
        )}
      </Row>
      <Row className="HeaderInfo bg-white">
        <Col id="PageInfo" className="d-flex gap-3 text-primary" xs={3} />
        <Col className="d-flex justify-content-between flex-nowrap {line-height:2.25rem;}" xs={6}>
          <div className="d-flex flex-column justify-content-center">
            <TimeZone
              icon={<PST width={30} />}
              className="text-primary"
              label="PST"
              timezone="America/Los_Angeles"
              onHover={(isHovering: boolean): void => setToolbarInfo(isHovering ? 'Pacific Time' : undefined)}
            />
          </div>
          <div className="d-flex flex-column justify-content-center">
            <TimeZone
              icon={<MST width={30} />}
              className="text-primary"
              label="MST"
              timezone="America/Boise"
              onHover={(isHovering: boolean): void => setToolbarInfo(isHovering ? 'Mountain Time' : undefined)}
            />
          </div>
          <div className="d-flex flex-column justify-content-center">
            <TimeZone
              icon={<CST width={30} />}
              className="text-primary"
              label="CST"
              timezone="America/Winnipeg"
              onHover={(isHovering: boolean): void => setToolbarInfo(isHovering ? 'Central Time' : undefined)}
            />
          </div>
          <div className="d-flex flex-column justify-content-center">
            <TimeZone
              icon={<EST width={30} />}
              className="text-primary"
              label="EST"
              timezone="America/New_York"
              onHover={(isHovering: boolean): void => setToolbarInfo(isHovering ? 'Eastern Time' : undefined)}
            />
          </div>
        </Col>
        <Col className="d-flex flex-column justify-content-center" xs={3}>
          <div className="d-flex justify-content-end">
            <div className="InformationToolbar text-left d-flex align-items-center">
              <i className="sv sv-notification-circle {font-size:1.5rem;}" />
              <span className="ps-1 text-primary">{toolbarInfo || 'Information Toolbar'}</span>
            </div>
          </div>
        </Col>
      </Row>
    </header>
  );
};

export default Header;
